import _ from 'lodash'
import 'foundation-sites'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Store } from 'vuex'
import RootState from '../../store/state'
import { Getters, Mutations } from '../../store/manager/helpers'
import { BaseData, ModalState } from '../../store/manager/state'
import { ContentOrderBy, ContentSortKey, isContentSortKey, isPublishStatus, PublishStatus } from '../../api/gerbera/core'
import { Watch } from 'vue-property-decorator'
import { Dictionary } from 'vue-router/types/router'

@Component
export default class ContentsToolBar extends Vue {
  name: 'ContentsToolBar'
  $store: Store<RootState>
  searchText: string = ''
  articlesStatus: string = ''

  get siteId(): number {
    return Getters.getAcountData(this.$store).kajikiId
  }

  get query(): Dictionary<string> {
    return this.$route.query
  }
  
  get baseData(): BaseData {
    return this.$store.state.manager.baseData
  }

  get selectedContents(): number[] {
    return Getters.selectedArticleIds(this.$store)
  }

  get isSelectAllChecked(): boolean {
    return this.selectedContents.length !== 0
  }

  set isSelectAllChecked(selected: boolean) {
    Mutations.updateAllContentSelection(this.$store, {selected})
  }

  get currentPageInfo(): string {
    return this.indexFrom + '－' + this.indexTo + ' / ' + this.totalCount
  }

  get currentPage(): number {
    return parseInt(this.query.page, 10) || 1
  }

  get perPage(): number {
    return 20
  }

  get totalCount(): number {
    return Getters.totalCount(this.$store)
  }

  get totalPage(): number {
    return Math.ceil(this.totalCount / this.perPage)
  }

  get hasPreviousPage(): boolean {
    return this.currentPage > 1
  }

  get hasNextPage(): boolean {
    return this.currentPage < this.totalPage
  }

  get indexFrom(): number {
    return Getters.indexFrom(this.$store)
  }

  get indexTo(): number {
    const total = this.totalCount
    return Math.min(this.currentPage * this.perPage, total)
  }

  get sortKey(): ContentSortKey | undefined {
    const sortKey = this.query.sortby
    return isContentSortKey(sortKey) ? sortKey : undefined
  }

  get isDescending(): boolean | undefined {
    const order = this.query.order
    if (order === 'asc') {
      return false
    }
    if (order === 'desc') {
      return true
    }
    return undefined
  }

  get orderBy(): ContentOrderBy[] {
    if (_.isUndefined(this.sortKey) && _.isUndefined(this.isDescending)) {
      return [{
        key: 'modifiedDate',
        isDescending: true
      }]
    }
    return [{
      key: this.sortKey || 'modifiedDate',
      isDescending: _.isUndefined(this.isDescending) ? false : this.isDescending
    }]
  }

  get orderByLabel(): string {
    const order = this.orderBy[0] || {key: '', isDescending: false}
    if (order.key === 'modifiedDate') {
      return order.isDescending ? '最終更新日が新しい順' : '最終更新日が古い順'
    }
    if (order.key === 'publishedDate') {
      return order.isDescending ? '公開日が新しい順' : '公開日が古い順'
    }
    if (order.key === 'fileSize') {
      return order.isDescending ? 'ファイルサイズが大きい順' : 'ファイルサイズが小さい順'
    }
    return '並べ替え'
  }

  get keyword(): string {
    return this.query.keyword
  }

  get status(): PublishStatus | undefined {
    const status = this.query.status
    return isPublishStatus(status) ? status : undefined
  }

  mounted(): void {
    this.refreshSearchText()
    this.refreshArticlesStatus()
  }

  @Watch('keyword')
  refreshSearchText(): void {
    this.searchText = this.keyword
  }

  @Watch('status')
  refreshArticlesStatus(): void {
    this.articlesStatus = this.status || ''
  }

  updateArticlesStatus() {
    this.updateQuery({
      status: this.articlesStatus,
      page: '1'
    })
  }

  trackSearchText(): void {
  }

  submitSearchText() {
    this.updateQuery({
      keyword: this.searchText,
      page: '1'
    })

    this.trackSearchText()
  }

  selected(modalState: ModalState) {
    Mutations.setModalState(this.$store, modalState)
    return this.$emit('toolbar-selected')
  }

  updateQuery(query: Dictionary<string>): void {
    const newQuery = Object.assign({}, this.query, query)

    this.$router.push({
      name: 'contents-manage',
      params: {
        kajiki_id: this.siteId.toString()
      },
      query: newQuery
    })
  }

  updateSortOrder(orderBy: ContentOrderBy): void {
    this.updateQuery({
      page: '1',
      sortby: orderBy.key,
      order: orderBy.isDescending ? 'desc' : 'asc'
    })
  }

  ascModifiedDate() {
    return this.updateSortOrder({
      key: 'modifiedDate',
      isDescending: true
    })
  }

  descModifiedDate() {
    return this.updateSortOrder({
      key: 'modifiedDate',
      isDescending: false
    })
  }

  ascPublishedDate() {
    return this.updateSortOrder({
      key: 'publishedDate',
      isDescending: true
    })
  }

  descPublishedDate() {
    return this.updateSortOrder({
      key: 'publishedDate',
      isDescending: false
    })
  }

  ascFileSize() {
    return this.updateSortOrder({
      key: 'fileSize',
      isDescending: true
    })
  }

  descFileSize() {
    return this.updateSortOrder({
      key: 'fileSize',
      isDescending: false
    })
  }

  changeCategory(): void {
    // カテゴリ選択ダイアログの初期値は、以下のようになる
    // * 選ばれた記事のカテゴリが1種類の場合、そのカテゴリを初期値にする
    // * 上記以外の場合、「未分類」を初期値とする
    const categories = Getters.selectedArticles(this.$store)
        .map(a => a.category)
        .map(c => c ? c.categoryId : undefined)
        .filter(id => !_.isUndefined(id))
    const uniqueCategories = _.uniq(categories)
    const modalState: ModalState = {
      modalName: 'change-category',
      data: {
        category: {
          categoryId: (uniqueCategories.length === 1) ? uniqueCategories[0] : null
        }
      }
    }
    this.selected(modalState)
  }

  changeStatus(): void {
    // TODO: 選択された記事から、適切なstatusの初期値を設定する
    const status = 'pending'
    const modalState: ModalState = {
      modalName: 'change-published',
      data: {
        status
      }
    }
    this.selected(modalState)
  }

  deleteContents(): void {
    const modalState: ModalState = {
      modalName: 'selected-item-delete',
      data: {}
    }
    this.selected(modalState)
  }

  nexPage(): void {
    if (!this.hasNextPage) {
      return
    }
    this.updateQuery({
      page: (this.currentPage + 1).toString()
    })
  }

  prevPage(): void {
    if (!this.hasPreviousPage) {
      return
    }
    this.updateQuery({
      page: (this.currentPage - 1).toString()
    })
  }

  onSearchFormInput(event: KeyboardEvent): void {
    if (event.keyCode === 13) {
      // 検索フォームで Enter が押されたら検索する
      this.submitSearchText()
    }
  }

  submitSearch(): void {
    this.updateArticlesStatus()
    this.submitSearchText()
  }
}
